import { ButtonLink, cn, RiArrowRightUpLine, Stack } from 'component-library';
import { ReactNode } from 'react';

import { useScreenSize } from '@/hooks/useScreenSize';

type DescriptionDefaultProps = {
  children: ReactNode;
  className?: string;
};

type ImageProps = {
  imageSrc: string;
  imageAlt: string;
};

const DescriptionContainerMobile = ({
  children,
  className,
  imageSrc,
  imageAlt,
}: DescriptionDefaultProps & ImageProps) => {
  const styles = cn('items-center', className);

  return (
    <Stack direction='col' spacing={8} className={styles}>
      <img src={imageSrc} alt={imageAlt} className='w-[300px]' />
      <Stack direction='col' spacing={2}>
        {children}
      </Stack>
    </Stack>
  );
};

const DescriptionContainerDesktop = ({
  children,
  className,
  imageSrc,
  imageAlt,
}: DescriptionDefaultProps & ImageProps) => {
  const styles = cn('items-center', className);

  return (
    <Stack direction='row' spacing={8} className={styles}>
      <img src={imageSrc} alt={imageAlt} className='w-[400px]' />
      <Stack direction='col' spacing={2}>
        {children}
      </Stack>
    </Stack>
  );
};

const DescriptionContainer = ({ ...delegated }: DescriptionDefaultProps & ImageProps) => {
  const isLargeScreen = useScreenSize() === 'large';

  if (!isLargeScreen) {
    return <DescriptionContainerMobile {...delegated} />;
  }

  return <DescriptionContainerDesktop {...delegated} />;
};

const DescriptionTitle = ({ children, className, ...delegated }: DescriptionDefaultProps) => {
  const styles = cn('text-text-primary typography-h3 my-2', className);

  return (
    <span {...delegated} className={styles}>
      {children}
    </span>
  );
};
const DescriptionSubtitle = ({ children, className, ...delegated }: DescriptionDefaultProps) => {
  const styles = cn('text-text-secondary typography-h4', className);

  return (
    <span {...delegated} className={styles}>
      {children}
    </span>
  );
};

const DescriptionBody = ({ children, className, ...delegated }: DescriptionDefaultProps) => {
  const styles = cn('text-text-primary typography-body1', className);

  return (
    <span {...delegated} className={styles}>
      {children}
    </span>
  );
};

const DescriptionLink = ({ children, className, to }: DescriptionDefaultProps & { to: string }) => {
  const styles = cn('w-fit my-2', className);

  return (
    <ButtonLink to={to} target='_blank' variant='outline' className={styles} rightAdornment={<RiArrowRightUpLine />}>
      {children}
    </ButtonLink>
  );
};

export const Description = {
  Container: DescriptionContainer,
  Title: DescriptionTitle,
  Subtitle: DescriptionSubtitle,
  Body: DescriptionBody,
  Link: DescriptionLink,
};
